import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  FileText,
  Phone,
  Mail,
  MapPin,
  Clock,
  Award,
  TrendingUp,
  Lock,
  Car,
  Menu,
  X,
  Sun,
  Moon,
  ArrowRight,
  Stamp,
  Gavel,
  ClipboardList,
  BarChart3,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Datos de contacto (un solo lugar para cambiarlos)                   */
/* ------------------------------------------------------------------ */
const WHATSAPP_NUMBER = '573246044125';
const WHATSAPP_DISPLAY = '324 604 4125';
const EMAIL = 'grupojuridico@totalcob.com';

/* Imágenes propias de TOTALCOB.
   - En el sitio publicado se usan las de la carpeta public_html/images del hosting.
   - En desarrollo (yarn start) se toman directamente de www.totalcob.com. */
const IMG = process.env.NODE_ENV === 'production' ? '/images' : 'https://www.totalcob.com/images';

const whatsappLink = (mensaje = 'Hola, quisiera información acerca de ') =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensaje)}`;

/* Ícono oficial de WhatsApp */
const WhatsAppIcon = ({ className = 'w-6 h-6' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* Aparición suave al hacer scroll */
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    observer.observe(node);
    // Respaldo: si el navegador no avisa del scroll, el contenido se muestra igual
    const fallback = setTimeout(() => setVisible(true), 2500);
    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-[1000ms] ease-soft motion-reduce:transition-none ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      } ${className}`}
    >
      {children}
    </div>
  );
};

/* Encabezado de sección consistente */
const SectionHeader = ({ eyebrow, title, subtitle, light = false }) => (
  <Reveal className="text-justify mb-14 md:mb-16 max-w-3xl mx-auto">
    {eyebrow && (
      <p className={`uppercase tracking-[0.25em] text-xs font-semibold mb-3 ${light ? 'text-neutral-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
        {eyebrow}
      </p>
    )}
    <h2 className={`font-display text-4xl md:text-5xl font-bold mb-5 ${light ? 'text-white' : 'text-neutral-900 dark:text-white'}`}>
      {title}
    </h2>
    <div className={`w-24 h-1 mx-auto ${light ? 'bg-neutral-400' : 'bg-neutral-600 dark:bg-neutral-400'}`} />
    {subtitle && (
      <p className={`mt-6 text-lg md:text-xl leading-relaxed ${light ? 'text-neutral-300' : 'text-neutral-600 dark:text-neutral-300'}`}>
        {subtitle}
      </p>
    )}
  </Reveal>
);

/* Tarjeta 3D: se inclina siguiendo el mouse y muestra un brillo que se mueve */
const Tilt3D = ({ children, className = '', max = 10, style }) => {
  const ref = useRef(null);
  const frame = useRef(0);

  const reset = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
    el.style.setProperty('--glare', '0');
  };

  const onMove = (e) => {
    const el = ref.current;
    if (!el || e.pointerType === 'touch') return;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      el.style.setProperty('--rx', `${(0.5 - y) * max}deg`);
      el.style.setProperty('--ry', `${(x - 0.5) * max}deg`);
      el.style.setProperty('--gx', `${x * 100}%`);
      el.style.setProperty('--gy', `${y * 100}%`);
      el.style.setProperty('--glare', '1');
    });
  };

  return (
    <div className={className} style={style}>
      <div ref={ref} onPointerMove={onMove} onPointerLeave={reset} className="tilt-3d relative">
        {children}
        <div className="tilt-glare pointer-events-none absolute inset-0 rounded-lg" aria-hidden="true" />
      </div>
    </div>
  );
};

/* Logos de clientes. Los nuevos se guardan en frontend/public/images (y en public_html/images del hosting) */
const CLIENTES = [
  { src: `${IMG}/cliente1.jpg`, alt: 'Cliente 1' },
  { src: `${IMG}/cliente2.jpg`, alt: 'Cliente 2' },
  { src: `${IMG}/cliente3.jpg`, alt: 'Cliente 3' },
  { src: `${IMG}/cliente4.jpg`, alt: 'Cliente 4' },
  { src: '/images/cliente5.png', alt: 'CTCOOP' },
];

const NAV_ITEMS = [
  { id: 'inicio', label: 'Inicio' },
  { id: 'nosotros', label: 'Nosotros' },
  { id: 'servicios', label: 'Servicios' },
  { id: 'clientes', label: 'Clientes' },
  { id: 'contacto', label: 'Contacto' },
];

const Home = () => {
  const [theme, setTheme] = useState('light');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');

  useEffect(() => {
    let savedTheme = 'light';
    try {
      savedTheme = localStorage.getItem('theme') || 'light';
    } catch (e) {
      /* almacenamiento no disponible */
    }
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  // Barra de navegación compacta al hacer scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resalta la sección visible en el menú
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    NAV_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    try {
      localStorage.setItem('theme', newTheme);
    } catch (e) {
      /* almacenamiento no disponible */
    }
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const servicios = [
    {
      icon: FileText,
      titulo: 'Servicios Jurídicos Integrales',
      descripcion: 'Asesoría y acompañamiento legal en diversas áreas del derecho, con enfoque estratégico, preventivo y corporativo.',
      descripcionCompleta:
        'Brindamos asesoría integral en diversas áreas del derecho comercial, civil, laboral y administrativo. Nuestro equipo desarrolla estrategias preventivas que protegen los intereses de su empresa, minimizando riesgos legales y optimizando procesos. Contamos con experiencia en contratos, sociedades, propiedad intelectual y cumplimiento normativo.',
      imagen: `${IMG}/servicios1.png`,
    },
    {
      icon: TrendingUp,
      titulo: 'Gestión y Recuperación de Cartera',
      descripcion: 'Procesos de cobro administrativo, prejurídico y jurídico orientados a maximizar recuperación de activos.',
      descripcionCompleta:
        'Especialistas en recuperación de cartera vencida mediante estrategias de cobro administrativo, prejurídico y judicial. Implementamos procesos de negociación efectivos que mantienen la relación comercial mientras recuperamos sus activos. Utilizamos tecnología de punta para seguimiento en tiempo real y reportes detallados de gestión.',
      imagen: `${IMG}/servicios2.png`,
    },
    {
      icon: Car,
      titulo: 'Accidentes de Tránsito y Responsabilidad Médica',
      descripcion: 'Representación especializada en reclamaciones por daños, perjuicios, lesiones e incidentes de responsabilidad.',
      descripcionCompleta:
        'Representación legal experta en casos de accidentes de tránsito y responsabilidad médica. Manejamos reclamaciones por lesiones personales, daños materiales, incapacidades y mala praxis médica. Nuestro equipo trabaja con peritos especializados para garantizar la documentación completa de daños y maximizar las indemnizaciones.',
      imagen: `${IMG}/servicios3.png`,
    },
    {
      icon: Stamp,
      titulo: 'Trámites y Servicios Notariales',
      descripcion: 'Asesoría y gestión profesional de trámites, diligencias notariales, autenticaciones, poderes y declaraciones.',
      descripcionCompleta:
        'Brindamos apoyo en la elaboración, revisión y formalización de documentos notariales, incluyendo autenticaciones, declaraciones extrajuicio, poderes, permisos de salida, compraventas, promesas de compraventa, constitución y cancelación de patrimonio de familia, escrituras públicas, sucesiones notariales, divorcios de mutuo acuerdo y demás actuaciones ante notaría. Acompañamos cada trámite con orientación jurídica clara para garantizar validez, cumplimiento y tranquilidad en cada gestión.',
      imagen: `${IMG}/servicios4.png`,
    },
  ];

  const ventajas = [
    {
      icon: Award,
      titulo: 'Respaldo Jurídico Especializado',
      descripcion: 'Enfoque estratégico y preventivo en cada proceso legal.',
    },
    {
      icon: TrendingUp,
      titulo: 'Recuperación Efectiva',
      descripcion: 'Cartera estructurada y orientada a resultados efectivos.',
    },
    {
      icon: Lock,
      titulo: 'Confidencialidad Absoluta',
      descripcion: 'Cumplimiento normativo en cada actuación jurídica.',
    },
  ];

  const stats = [
    { valor: '15+', label: 'Años de experiencia' },
    { valor: '100%', label: 'Compromiso' },
    { valor: '24/7', label: 'Disponibilidad' },
  ];

  const contactoItems = [
    {
      icon: MapPin,
      titulo: 'Ubicación',
      contenido: (
        <>
          Calle 20 #6-30, Oficina 302
          <br />
          Edificio Banco Ganadero
          <br />
          Pereira, Colombia
        </>
      ),
    },
    {
      icon: Mail,
      titulo: 'Correo electrónico',
      contenido: (
        <a href={`mailto:${EMAIL}`} className="font-medium text-neutral-800 dark:text-neutral-100 hover:underline break-all">
          {EMAIL}
        </a>
      ),
    },
    {
      icon: Clock,
      titulo: 'Horario',
      contenido: (
        <>
          Lunes a viernes
          <br />
          8:00 a. m. – 6:00 p. m.
        </>
      ),
    },
  ];

  const navLinkClass = (id) =>
    `relative py-1 font-medium transition-colors after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:rounded-full after:bg-current after:transition-all after:duration-300 ${
      activeSection === id
        ? 'text-neutral-900 dark:text-white after:w-full'
        : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white after:w-0 hover:after:w-full'
    }`;

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-950 text-neutral-900 dark:text-white transition-colors duration-300 overflow-x-hidden">
      {/* ============================ NAVEGACIÓN ============================ */}
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          mobileMenuOpen
            ? 'bg-white dark:bg-neutral-900 shadow-lg border-b border-neutral-200 dark:border-neutral-800'
            : 'bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm shadow-sm border-b border-neutral-200/70 dark:border-neutral-800/70'
        }`}
      >
        <div className={`container mx-auto px-4 transition-all duration-300 ${scrolled ? 'py-2' : 'py-3'}`}>
          <div className="flex justify-between items-center">
            <button onClick={() => scrollToSection('inicio')} className="flex items-center gap-3 text-left" aria-label="Ir al inicio">
              <img
                src={`${IMG}/logo3.jpg`}
                alt="Logo TOTALCOB"
                className={`w-auto rounded-md object-contain transition-all duration-300 ${scrolled ? 'h-11' : 'h-12'}`}
              />
              <span>
                <span className="block text-lg font-bold leading-tight tracking-tight text-neutral-900 dark:text-white">TOTALCOB S.A.S</span>
                <span className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">Grupo Jurídico</span>
              </span>
            </button>

            {/* Menú escritorio */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_ITEMS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  aria-current={activeSection === id ? 'true' : undefined}
                  className={navLinkClass(id)}
                >
                  {label}
                </button>
              ))}

              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${
                  'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }`}
                aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>

            {/* Botones móvil */}
            <div className="md:hidden flex items-center gap-1">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${
                  'text-neutral-700 dark:text-neutral-300'
                }`}
                aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-full transition-colors ${
                  'text-neutral-900 dark:text-white'
                }`}
                aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Menú desplegable móvil */}
          <div
            className={`md:hidden grid transition-all duration-300 ease-out ${
              mobileMenuOpen ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="overflow-hidden">
              <div className="flex flex-col gap-1 border-t border-neutral-200 dark:border-neutral-800 pt-4 pb-2">
                {NAV_ITEMS.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => scrollToSection(id)}
                    className={`text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                      activeSection === id
                        ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-neutral-700 hover:bg-neutral-800 dark:bg-neutral-600 dark:hover:bg-neutral-500 text-white font-semibold px-4 py-3 transition-colors"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  Escríbenos por WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* ======================= BOTÓN FLOTANTE WHATSAPP ======================= */}
      <a
        href={whatsappLink()}
        target="_blank"
        rel="noopener noreferrer"
        className="group fixed bottom-20 right-5 z-50 flex items-center gap-3"
        aria-label={`Escríbenos por WhatsApp al ${WHATSAPP_DISPLAY}`}
      >
        <span className="hidden md:block pointer-events-none rounded-full bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 text-sm font-medium px-4 py-2 shadow-lg opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
          ¿Hablamos? {WHATSAPP_DISPLAY}
        </span>
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-green-500 group-hover:bg-green-600 text-white shadow-2xl transition-all duration-500 ease-soft group-hover:scale-105">
          <span className="absolute inset-0 rounded-full bg-green-500 opacity-40 animate-ping motion-reduce:hidden" />
          <WhatsAppIcon className="relative w-7 h-7" />
        </span>
      </a>

      {/* ================================ HERO ================================ */}
      {/* Portada corporativa construida en código: textos nítidos en cualquier pantalla,
          con los mismos datos de la imagen anterior (logo, cobranza, gestión de cartera y resultados) */}
      <section id="inicio" className="relative overflow-hidden bg-neutral-950 text-white pt-[68px] lg:min-h-[100svh] lg:flex lg:flex-col">
        {/* Fotografía (lado derecho en escritorio, arriba en celular) */}
        <div className="relative overflow-hidden h-64 sm:h-80 lg:h-auto lg:absolute lg:inset-y-0 lg:right-0 lg:w-[58%] hero-zoom-in">
          <img
            src={`${IMG}/servicios2.png`}
            alt=""
            aria-hidden="true"
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[200%] sm:w-[165%] max-w-none h-auto lg:top-0 lg:translate-y-0 lg:w-full lg:h-full lg:object-cover lg:object-[60%_center]"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/20 to-transparent lg:bg-gradient-to-r lg:from-neutral-950 lg:via-neutral-950/40 lg:to-transparent" />
          <div className="absolute inset-0 bg-neutral-950/10" />
        </div>

        {/* Línea diagonal plateada que separa el panel de la foto */}
        <div className="hidden lg:block absolute inset-y-0 left-[45%] w-[3px] -skew-x-[8deg] bg-gradient-to-b from-neutral-500/0 via-neutral-300 to-neutral-500/0 opacity-70" aria-hidden="true" />
        {/* Textura sutil */}
        <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:22px_22px] pointer-events-none" aria-hidden="true" />

        <div className="relative container mx-auto px-4 lg:flex-1 lg:flex lg:items-center">
          <div className="max-w-xl lg:max-w-[40%] py-12 lg:py-16">
            <div className="fade-up">
              <span className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.35em] text-neutral-400">
                <span className="h-px w-10 bg-neutral-500" />
                Recuperación de cartera · Servicios jurídicos
              </span>
            </div>

            <div className="fade-up" style={{ animationDelay: '270ms' }}>
              <h1 className="font-display mt-6 text-4xl sm:text-5xl xl:text-6xl font-bold leading-[1.08]">
                Recuperamos su cartera con respaldo jurídico
              </h1>
              <p className="mt-5 text-lg text-neutral-300 leading-relaxed max-w-lg">
                Más de 15 años en el Eje Cafetero acompañando a empresas y personas en procesos de cobro, conciliación y
                defensa legal.
              </p>
            </div>

            <div className="fade-up" style={{ animationDelay: '390ms' }}>
              <ul className="mt-10 pt-8 border-t border-neutral-800 space-y-5">
                {[
                  { icon: Gavel, t1: 'Cobranza', t2: 'Prejurídica y jurídica' },
                  { icon: ClipboardList, t1: 'Gestión estratégica', t2: 'De cartera' },
                  { icon: BarChart3, t1: 'Resultados', t2: 'Comprobables' },
                ].map(({ icon: Icon, t1, t2 }) => (
                  <li key={t1} className="flex items-center gap-5">
                    <span className="flex items-center justify-center w-12 h-12 rounded-md border border-neutral-600 bg-white/5 text-neutral-200 shrink-0">
                      <Icon className="w-6 h-6" />
                    </span>
                    <span className="h-10 w-px bg-neutral-700" />
                    <span>
                      <span className="block text-lg font-bold uppercase tracking-wide leading-tight">{t1}</span>
                      <span className="block text-sm uppercase tracking-[0.15em] text-neutral-400">{t2}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="fade-up" style={{ animationDelay: '510ms' }}>
              <div className="mt-12 flex flex-col sm:flex-row gap-3">
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-white hover:bg-neutral-200 text-neutral-900 font-semibold px-6 py-3.5 transition-colors duration-500 ease-soft"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  Hablar con un abogado
                </a>
                <button
                  onClick={() => scrollToSection('servicios')}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-neutral-500 hover:border-white hover:bg-white/10 text-white font-semibold px-6 py-3.5 transition-all duration-500 ease-soft"
                >
                  Conocer servicios
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Barra de datos clave */}
        <div className="relative border-t border-white/10 bg-neutral-900/90 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6 items-center">
            {[
              { valor: '15+', label: 'Años de experiencia' },
              { valor: '100%', label: 'Compromiso con el cliente' },
              { valor: '24/7', label: 'Disponibilidad' },
            ].map((d) => (
              <div key={d.label} className="flex items-center gap-3 sm:border-l sm:border-neutral-700 sm:pl-6 first:border-l-0 first:pl-0">
                <span className="font-display text-2xl md:text-3xl font-bold text-white">{d.valor}</span>
                <span className="text-[11px] uppercase tracking-[0.15em] text-neutral-400 leading-tight">{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== NOSOTROS ============================== */}
      <section id="nosotros" className="py-24 px-4 bg-gradient-to-b from-neutral-100 to-white dark:from-neutral-950 dark:to-neutral-950 transition-colors duration-300 scroll-mt-16">
        <div className="container mx-auto">
          <SectionHeader eyebrow="Quiénes somos" title="Nosotros" />

          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <Reveal className="relative">
              <div className="absolute -inset-3 md:-inset-4 rounded-3xl border border-neutral-200 dark:border-neutral-800 -z-0 hidden md:block translate-x-4 translate-y-4" />
              <div className="group relative overflow-hidden rounded-md shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)]">
                <img
                  src={`${IMG}/nosotros.png`}
                  alt="Equipo TOTALCOB"
                  loading="lazy"
                  className="img-soft w-full h-[380px] md:h-[480px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 left-6 md:left-auto md:-right-6 bg-neutral-700 dark:bg-neutral-600 text-white rounded-md px-6 py-4 shadow-lg">
                <div className="font-display text-3xl font-bold leading-none">15+</div>
                <div className="text-xs uppercase tracking-widest mt-1 opacity-80">Años de experiencia</div>
              </div>
            </Reveal>

            <Reveal delay={150} className="space-y-6 pt-6 md:pt-0">
              <h3 className="font-display text-3xl font-bold text-neutral-900 dark:text-white">TOTALCOB S.A.S – Grupo Jurídico</h3>
              <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed md:text-justify">
                Especialistas en recuperación de cartera administrativa, prejurídica y judicial. Contamos con un equipo de
                profesionales con más de <span className="font-semibold text-neutral-900 dark:text-white">15 años de experiencia</span> en
                el Eje Cafetero, enfocado en procesos de conciliación, negociación y cobro jurídico efectivo.
              </p>
              <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed md:text-justify">
                Nuestro trabajo se fundamenta en estrategias legales sólidas, comunicación asertiva y seguimiento permanente en
                cada etapa del proceso, garantizando a nuestros clientes un acompañamiento continuo y soluciones orientadas a
                resultados reales.
              </p>
              <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed md:text-justify">
                Tenemos como propósito principal lograr la recuperación oportuna de saldos adeudados por personas naturales y
                jurídicas, contribuyendo a la protección financiera y estabilidad de nuestros aliados.
              </p>

              <div className="grid grid-cols-3 gap-4 pt-4">
                {stats.map((s) => (
                  <div key={s.label} className="card-pro text-center rounded-md border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 py-5 px-2">
                    <div className="font-display text-3xl md:text-4xl font-bold text-neutral-700 dark:text-neutral-300">{s.valor}</div>
                    <div className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mt-2">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================== SERVICIOS ============================== */}
      <section id="servicios" className="py-24 px-4 bg-neutral-200/70 dark:bg-neutral-900 transition-colors duration-300 scroll-mt-16">
        <div className="container mx-auto">
          <SectionHeader
            eyebrow="Servicios"
            title="Soluciones jurídicas especializadas"
            subtitle="Servicios estructurados para proteger intereses, recuperar activos y fortalecer la gestión legal de nuestros clientes."
          />

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {servicios.map((servicio, index) => {
              const Icon = servicio.icon;
              const numero = String(index + 1).padStart(2, '0');
              return (
                <Reveal key={servicio.titulo} delay={(index % 2) * 120} className="h-full">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="card-pro group relative flex flex-col w-full h-full text-left rounded-md overflow-hidden bg-white dark:bg-neutral-800 border border-neutral-200/80 dark:border-neutral-700/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500">
                        <span className="card-line absolute top-0 left-0 z-10 h-[2px] bg-neutral-600 dark:bg-neutral-400" />
                        {/* Imagen con tratamiento monocromático que se colorea al pasar el mouse */}
                        <div className="relative aspect-[16/9] overflow-hidden bg-neutral-200 dark:bg-neutral-700">
                          <img
                            src={servicio.imagen}
                            alt={servicio.titulo}
                            loading="lazy"
                            className="img-soft w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 via-neutral-900/10 to-transparent" />
                        </div>

                        <div className="relative flex flex-col flex-1 p-7 md:p-8">
                          <span className="absolute -top-7 right-7 flex items-center justify-center w-14 h-14 rounded-md bg-neutral-800 dark:bg-neutral-500 text-white shadow-md ring-4 ring-white dark:ring-neutral-800 transition-colors duration-700 ease-soft group-hover:bg-neutral-600 dark:group-hover:bg-neutral-400">
                            <Icon className="w-6 h-6" />
                          </span>
                          <h3 className="font-display text-xl md:text-2xl font-bold text-neutral-900 dark:text-white pr-16 leading-snug">{servicio.titulo}</h3>
                          <div className="w-10 h-[2px] bg-neutral-300 dark:bg-neutral-600 my-4 transition-all duration-700 ease-soft group-hover:w-16 group-hover:bg-neutral-500" />
                          <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed flex-1">{servicio.descripcion}</p>
                          <div className="mt-6 pt-5 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
                            <span className="text-sm font-semibold uppercase tracking-wider text-neutral-700 dark:text-neutral-200">Conocer servicio</span>
                            <span className="flex items-center justify-center w-9 h-9 rounded-md border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 group-hover:bg-neutral-700 group-hover:border-neutral-700 group-hover:text-white dark:group-hover:bg-neutral-500 transition-all duration-700 ease-soft">
                              <ArrowRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 p-0">
                      <div className="relative">
                        <img
                          src={servicio.imagen}
                          alt={servicio.titulo}
                          className="w-full h-56 md:h-72 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/70 to-transparent" />
                        <span className="absolute bottom-5 left-6 md:left-8 flex items-center gap-3 text-white">
                          <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-neutral-600/90 backdrop-blur-sm">
                            <Icon className="w-5 h-5" />
                          </span>
                        </span>
                      </div>
                      <div className="p-6 md:p-8 space-y-5">
                        <DialogHeader>
                          <DialogTitle className="font-display text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-left">
                            {servicio.titulo}
                          </DialogTitle>
                        </DialogHeader>
                        <DialogDescription className="text-base md:text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed md:text-justify">
                          {servicio.descripcionCompleta}
                        </DialogDescription>
                        <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-2">
                          <Button
                            variant="outline"
                            onClick={() => scrollToSection('contacto')}
                            className="border-neutral-300 dark:border-neutral-600 dark:text-white"
                          >
                            Ver datos de contacto
                          </Button>
                          <a
                            href={whatsappLink(`Hola, quisiera información sobre ${servicio.titulo}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-neutral-700 hover:bg-neutral-800 dark:bg-neutral-600 dark:hover:bg-neutral-500 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                          >
                            <WhatsAppIcon className="w-4 h-4" />
                            Solicitar información
                          </a>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* =========================== POR QUÉ ELEGIRNOS =========================== */}
      <section className="py-24 px-4 bg-gradient-to-b from-neutral-100 to-neutral-50 dark:from-neutral-950 dark:to-neutral-950 transition-colors duration-300">
        <div className="container mx-auto">
          <SectionHeader
            eyebrow="¿Por qué elegirnos?"
            title="Expertos en Soluciones Legales"
            subtitle="TOTALCOB S.A.S. Grupo Jurídico está conformado por un equipo de abogados y gestores altamente capacitados en recuperación de cartera, que combina conocimiento jurídico, experiencia estratégica y herramientas tecnológicas para diseñar soluciones efectivas, optimizar los procesos de cobro y maximizar los resultados para cada cliente."
          />

          <Reveal className="max-w-3xl mx-auto mb-14">
            <p className="text-lg text-neutral-600 dark:text-neutral-300 text-justify leading-relaxed">
              Un servicio basado en principios de{' '}
              <span className="font-semibold text-neutral-900 dark:text-white">honestidad, eficiencia, control y cumplimiento normativo</span>,
              garantizando procesos seguros, transparentes y efectivos en cada etapa de la gestión.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-20 max-w-6xl mx-auto">
            {ventajas.map((ventaja, index) => {
              const Icon = ventaja.icon;
              return (
                <Reveal key={ventaja.titulo} delay={index * 120} className="h-full">
                  <div className="card-pro group relative h-full p-8 lg:p-10 rounded-md bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-800 overflow-hidden">
                    {/* Línea superior que se completa al pasar el mouse */}
                    <span className="card-line absolute top-0 left-0 h-[2px] bg-neutral-600 dark:bg-neutral-400" />
                    <span className="absolute top-6 right-7 font-display text-5xl font-bold text-neutral-100 dark:text-neutral-800 select-none">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="relative flex items-center justify-center w-14 h-14 rounded-md border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 mb-6 group-hover:bg-neutral-600 group-hover:border-neutral-600 group-hover:text-white dark:group-hover:bg-neutral-500 transition-all duration-700 ease-soft">
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="relative font-display text-xl font-bold text-neutral-900 dark:text-white mb-3">{ventaja.titulo}</h3>
                    <p className="relative text-neutral-600 dark:text-neutral-300 leading-relaxed">{ventaja.descripcion}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Galería */}
          <Reveal className="grid md:grid-cols-2 gap-5 max-w-6xl mx-auto">
            {[
              {
                src: `${IMG}/respaldo.png`,
                alt: 'Equipo profesional TOTALCOB',
                titulo: 'Respaldo jurídico especializado',
                texto: 'Enfoque estratégico y preventivo en cada proceso legal',
                clase: 'md:row-span-2 h-[300px] md:h-full md:min-h-[540px]',
                w: 1400,
              },
              {
                src: `${IMG}/recuperacion.png`,
                alt: 'Profesionales trabajando',
                titulo: 'Recuperación efectiva',
                texto: 'Cartera estructurada y orientada a resultados efectivos',
                clase: 'h-[260px]',
                w: 1000,
              },
              {
                src: '/images/confidencial-sin-candado.svg',
                alt: 'Documentos confidenciales',
                titulo: 'Confidencialidad absoluta',
                texto: 'Cumplimiento normativo en cada actuación jurídica',
                clase: 'h-[260px]',
                w: 1000,
              },
            ].map((foto) => (
              <figure key={foto.titulo} className={`card-pro group relative overflow-hidden rounded-md bg-neutral-200 dark:bg-neutral-800 ${foto.clase}`}>
                <img
                  src={foto.src}
                  alt={foto.alt}
                  loading="lazy"
                  className={`img-soft absolute object-cover ${foto.imgClase || 'inset-0 w-full h-full'}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 via-neutral-900/10 to-transparent" />
                <figcaption className="absolute bottom-0 inset-x-0 p-6 text-white">
                  <span className="block w-8 h-[2px] bg-white/70 mb-3 transition-all duration-700 ease-soft group-hover:w-14" />
                  <span className="block font-display text-xl md:text-2xl font-bold">{foto.titulo}</span>
                  <span className="block text-sm text-neutral-200 mt-1">{foto.texto}</span>
                </figcaption>
              </figure>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ============================== CLIENTES ============================== */}
      {/* Sección oscura con tarjetas 3D que se inclinan siguiendo el mouse */}
      <section id="clientes" className="relative py-28 px-4 overflow-hidden bg-gradient-to-b from-neutral-900 via-neutral-950 to-neutral-900 scroll-mt-16">
        <div className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.6)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" aria-hidden="true" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-neutral-500/20 blur-[120px]" aria-hidden="true" />

        <div className="relative container mx-auto">
          <SectionHeader
            light
            eyebrow="Clientes y aliados"
            title="Empresas que confían en nosotros"
            subtitle="Construimos relaciones sólidas con organizaciones que valoran la excelencia, la confianza y los resultados."
          />

          <div className="flex flex-wrap justify-center gap-8 max-w-6xl mx-auto [perspective:1200px]">
            {CLIENTES.map((cliente, index) => (
              <Reveal key={cliente.src} delay={(index % 3) * 100} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.4rem)]">
                <Tilt3D max={14} className="float-soft" style={{ animationDelay: `${index * -1.5}s` }}>
                  <div className="relative h-60 md:h-72 rounded-lg bg-gradient-to-br from-white via-white to-neutral-200 border border-white/60 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,255,255,0.05)] flex items-center justify-center p-8 overflow-hidden [transform-style:preserve-3d]">
                    {/* Borde plateado interior */}
                    <div className="absolute inset-2 rounded-md border border-neutral-300/70 pointer-events-none" />
                    <img
                      src={cliente.src}
                      alt={cliente.alt}
                      loading="lazy"
                      className="relative max-h-[78%] max-w-[85%] object-contain [transform:translateZ(50px)] drop-shadow-[0_12px_14px_rgba(0,0,0,0.18)]"
                    />
                  </div>
                  {/* Reflejo en el piso */}
                  <div className="mx-auto mt-4 h-4 w-3/4 rounded-[50%] bg-black/60 blur-md" aria-hidden="true" />
                </Tilt3D>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ BANDA DE LLAMADO ============================ */}
      <section className="relative px-4 py-20 bg-neutral-900 dark:bg-black overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px]" />
        <Reveal className="relative container mx-auto max-w-4xl text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white">¿Tiene cartera pendiente por recuperar?</h2>
          <p className="mt-4 text-lg text-neutral-300">
            Cuéntenos su caso. Le respondemos de inmediato y le proponemos la mejor estrategia.
          </p>
          <a
            href={whatsappLink('Hola, quisiera una asesoría sobre recuperación de cartera')}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-white hover:bg-neutral-200 text-neutral-900 font-semibold px-8 py-4 shadow-lg transition-all duration-500 ease-soft"
          >
            <WhatsAppIcon className="w-5 h-5" />
            Escribir al {WHATSAPP_DISPLAY}
          </a>
        </Reveal>
      </section>

      {/* ============================== CONTACTO ============================== */}
      <section id="contacto" className="py-24 px-4 bg-gradient-to-b from-neutral-100 to-neutral-200/60 dark:from-neutral-950 dark:to-neutral-950 transition-colors duration-300 scroll-mt-16">
        <div className="container mx-auto">
          <SectionHeader
            eyebrow="Hablemos"
            title="Contacto"
            subtitle="Estamos disponibles para asesorarle de manera profesional"
          />

          <div className="grid lg:grid-cols-5 gap-8 max-w-6xl mx-auto">
            <Reveal className="lg:col-span-2">
              <div className="h-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-sm overflow-hidden">
                {/* Cabecera con WhatsApp */}
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-neutral-800 dark:bg-neutral-800 hover:bg-neutral-700 text-white p-7 transition-colors"
                >
                  <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-300">
                    <WhatsAppIcon className="w-4 h-4" />
                    Atención inmediata · WhatsApp
                  </span>
                  <span className="mt-3 flex items-center justify-between">
                    <span className="font-display text-3xl font-bold tracking-wide">{WHATSAPP_DISPLAY}</span>
                    <span className="flex items-center justify-center w-10 h-10 rounded-md border border-white/30 group-hover:bg-white group-hover:text-neutral-900 transition-all">
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </span>
                </a>

                <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  {contactoItems.map(({ icon: Icon, titulo, contenido }) => (
                    <li key={titulo} className="flex items-start gap-4 p-7">
                      <span className="flex items-center justify-center w-11 h-11 rounded-md border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 shrink-0">
                        <Icon className="w-5 h-5" />
                      </span>
                      <div>
                        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 dark:text-neutral-400 mb-1.5">{titulo}</h3>
                        <p className="text-neutral-800 dark:text-neutral-200 leading-relaxed">{contenido}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={150} className="lg:col-span-3 min-h-[420px] lg:min-h-full">
              <div className="w-full h-full min-h-[420px] rounded-md overflow-hidden shadow-sm border border-neutral-200 dark:border-neutral-800 transition-all duration-500">
                <iframe
                  title="Ubicación TOTALCOB S.A.S"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3976.6159876447643!2d-75.69874368573795!3d4.814288796608598!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8e38874c8c8c8c8d%3A0x8c8c8c8c8c8c8c8c!2sCalle%2020%20%236-30%2C%20Pereira%2C%20Risaralda!5e0!3m2!1ses!2sco!4v1234567890123!5m2!1ses!2sco"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full min-h-[420px]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================================ FOOTER ================================ */}
      <footer className="bg-neutral-900 dark:bg-black text-white pt-16 pb-8 px-4 border-t border-neutral-800">
        <div className="container mx-auto max-w-6xl">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={`${IMG}/logo3.jpg`} alt="Logo TOTALCOB" className="w-14 h-14 rounded-lg object-contain bg-white" />
                <div>
                  <div className="text-lg font-bold leading-tight">TOTALCOB S.A.S</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-400">Grupo Jurídico</div>
                </div>
              </div>
              <p className="text-neutral-400 text-sm leading-relaxed">
                Grupo jurídico especializado en recuperación de cartera, con soluciones eficientes, seguras y con respaldo legal.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Navegación</h4>
              <ul className="space-y-2 text-sm text-neutral-400">
                {NAV_ITEMS.map(({ id, label }) => (
                  <li key={id}>
                    <button onClick={() => scrollToSection(id)} className="hover:text-white transition-colors">
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Servicios</h4>
              <ul className="space-y-2 text-sm text-neutral-400">
                <li>Recuperación de cartera</li>
                <li>Asesoría jurídica</li>
                <li>Procesos civiles</li>
                <li>Accidentes de tránsito</li>
                <li>Responsabilidad médica</li>
                <li>Trámites notariales</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contacto</h4>
              <ul className="space-y-3 text-sm text-neutral-400">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Calle 20 #6-30, Of. 302, Pereira</span>
                </li>
                <li>
                  <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="w-4 h-4 shrink-0" />
                    <span>{WHATSAPP_DISPLAY}</span>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-white transition-colors break-all">
                    <Mail className="w-4 h-4 shrink-0" />
                    <span>{EMAIL}</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-neutral-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-neutral-500 text-sm">
            <p>© {new Date().getFullYear()} TOTALCOB S.A.S — Todos los derechos reservados</p>
            <p>Pereira, Risaralda · Colombia</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
